import { makeAutoObservable, toJS, computed } from "mobx";
import { ColumnFiltersState, SortingState, VisibilityState, RowSelectionState } from "@tanstack/react-table";
interface DefaultInitialValues {
	page?: number;
	pageSize?: number;
	sorting?: SortingState;
	filters?: ColumnFiltersState;
	columnVisibility?: VisibilityState;
}

export type InitialValues = DefaultInitialValues | null;

export class TableStateStore {
	// Core state
	sorting: SortingState = [];
	columnFilters: ColumnFiltersState = [];
	columnVisibility: VisibilityState = {};
	rowSelection: RowSelectionState = {};

	page = 0;
	pageSize = 10;

	constructor(initialValues: InitialValues) {
		makeAutoObservable(this, {
			filtersSnapshot: computed,
			columnVisibilitySnaphot: computed,
		});
		this.page = initialValues?.page || 0;
		this.pageSize = initialValues?.pageSize || 10;
		this.sorting = initialValues?.sorting || [];
		this.columnFilters = initialValues?.filters || [];
		this.columnVisibility = initialValues?.columnVisibility || {};
	}
	get filtersSnapshot() {
		return toJS(this.columnFilters);
	}

	get columnVisibilitySnaphot() {
		return toJS(this.columnVisibility);
	}

	get sortingSnaphot() {
		return toJS(this.sorting);
	}

	get rowSelectionSnapshot() {
		return toJS(this.rowSelection);
	}

	setRowSelection = (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
		this.rowSelection = typeof updaterOrValue === "function" ? updaterOrValue(this.rowSelection) : updaterOrValue;
	};

	// ---- Sorting ----
	setSorting = (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
		this.sorting = typeof updaterOrValue === "function" ? updaterOrValue(this.sorting) : updaterOrValue;
	};

	// ---- Filters ----
	setFilters = (updaterOrValue: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
		this.columnFilters = typeof updaterOrValue === "function" ? updaterOrValue(this.columnFilters) : updaterOrValue;
	};

	setFilterValue = (columnId: string, value: unknown) => {
		const index = this.columnFilters.findIndex(f => f.id === columnId);
		if (index >= 0) {
			this.columnFilters[index].value = value;
		} else {
			this.columnFilters.push({ id: columnId, value });
		}
		this.page = 0;
	};

	clearFilter = (columnId: string) => {
		const idx = this.columnFilters.findIndex(f => f.id === columnId);
		if (idx !== -1) {
			this.columnFilters.splice(idx, 1);
			this.page = 0;
		}
	};

	clearFilters = () => {
		this.columnFilters = [];
	};

	clearRowSelection = () => {
		this.rowSelection = {};
	};

	// ---- Column Visibility ----
	setColumnVisibility = (updaterOrValue: VisibilityState | ((old: VisibilityState) => VisibilityState)) => {
		if (typeof updaterOrValue === "function") {
			this.columnVisibility = updaterOrValue(this.columnVisibility);
		} else {
			this.columnVisibility = updaterOrValue;
		}
	};

	toggleColumn = (columnId: string) => {
		const columns = Object.keys(this.columnVisibility);
		if (columns.includes(columnId)) {
			this.columnVisibility[columnId] = !this.columnVisibility[columnId];
		} else {
			this.columnVisibility[columnId] = false;
		}
	};

	// ---- Pagination ----
	setPage = (page: number) => {
		this.page = page;
	};

	setPageSize = (size: number) => {
		this.pageSize = size;
		this.page = 0;
	};

	resetPagination = () => {
		this.page = 0;
		this.pageSize = 10;
	};

	// ---- General Reset ----
	resetAll = () => {
		this.sorting = [];
		this.columnFilters = [];
		this.columnVisibility = {};
		this.resetPagination();
	};
}
